import { Component, computed, OnDestroy, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription, withLatestFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { updateVisitHistoryData } from '../../../../../store/global/global.actions';
import { BreadcrumbsEnum } from '../../../../shared/models/breadcrumbs.enum';
import { BreadcrumbService } from 'xng-breadcrumb';
import { CenterObject } from '@it2go/types';
import { CenterDetailPageStore } from './center-detail-page.store';
import { AsyncPipe } from '@angular/common';
import {
    CenterBasicInfosComponent,
} from '../../../components/organization/center-basic-info/center-basic-infos.component';
import {
    OrganizationAdministrationAddressesTileComponent,
} from '../../../components/organization/organization-administration-addresses-tile/organization-administration-addresses-tile.component';
import {
    OrganizationContactPersonsTableComponent,
} from '../../../components/organization/organization-contact-persons-table/organization-contact-persons-table.component';
import { TranslateService } from '@ngx-translate/core';
import { CardComponent } from '../../../../../components/kit/card/card.component';
import { MultiSelectInputComponent } from '../../../../../components/kit/input/multi-select-input/multi-select-input.component';
import { SaveButtonComponent } from '../../../../../components/kit/button/save-button.component';
import { AppFormGroup } from '../../../../../utils/form.utils';
import { fb } from '../../../../shared/shared.global-service';
import { InfiniteScrollStore } from '../../../../shared/store/common/infinite-scroll.store';
import { centerManagerUserFrag } from './center-detail-page.query';
import { FormatPersonPipe } from '../../../../shared/pipes/format-person.pipe';
import { contentGlobalSuccess } from '../../../content.error';
import { FilterBuilder } from '@libs/shared/utils/grid.builder';
import { CenterDetailPageStore2 } from './center-detail-page.store2';
import { featureOn } from '../../../../../feature-flag';

@Component({
    selector: 'app-center-detail-page',
    templateUrl: 'center-detail-page.component.html',
    providers: [
        CenterDetailPageStore,
        CenterDetailPageStore2,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    CenterBasicInfosComponent,
    OrganizationAdministrationAddressesTileComponent,
    OrganizationContactPersonsTableComponent,
    AsyncPipe,
    CardComponent,
    MultiSelectInputComponent,
    SaveButtonComponent,
    FormatPersonPipe,
],
})
export class CenterDetailPageComponent implements OnInit, OnDestroy {

    protected readonly featureOn = featureOn;
    protected readonly store = inject(CenterDetailPageStore);
    protected readonly store2 = inject(CenterDetailPageStore2);
    private readonly route = inject(ActivatedRoute);
    private readonly translate = inject(TranslateService);
    private readonly breadcrumbService = inject(BreadcrumbService);

    center$ = this.store.data.organization$.pipe(
        withLatestFrom(this.store.routeParam.centerId),
        map(([organization, centerId]) =>
            organization?.centers?.find((it) => it.id.toString() == centerId),
        ),
    ) as Observable<CenterObject | null>;

    centerSubscription: Subscription = new Subscription();

    protected readonly managersForm = new AppFormGroup({
        userIds: fb.control<string[]>([]),
    });

    protected readonly usersScroll = computed(() => {
        const organizationId = this.store.organization()?.id;
        if (!organizationId) return null;

        return new InfiniteScrollStore(
            this.store2.api.getUsers,
            FilterBuilder.filter({
                organizationServiceManagerId: organizationId,
                limit: 100,
            }),
            true,
            centerManagerUserFrag,
        );
    });

    constructor() {
        const store = this.store;
        store.onRouteChange('getOrganization', { id: 'organizationId' });
        store.doOnActions(
            async () => {
                const organizationId = await store.get(
                    store.routeParam.organizationId,
                );
                if (organizationId) {
                    store.api.getOrganization({
                        id: Number(organizationId),
                        centerId: null,
                        validAt: null,
                    });
                }
            },
            'linkPerson',
            'unlinkPerson',
            'createPerson',
            'updatePerson',
            'deleteCenter',
            'createBasicInfo',
            'updateBasicInfo',
            'deleteBasicInfo',
            'deleteAddress',
            'updateAddress',
            'createAddress',
            'updateCenter',
        );
    }

    ngOnInit(): void {
        this.centerSubscription = this.center$.subscribe((center) => {
            if (center && featureOn.SERVICE_CENTER_MANAGER) {
                void this.loadManagers(center.id);
            }
            const centerName =
                center?.basicInfos?.find((info) => {
                    return (
                        new Date(info.validFrom) <= new Date() &&
                        (info.validTo === null ||
                            new Date(info.validTo) >= new Date())
                    );
                })?.name ?? this.translate.instant('database.organizationAdministrationPage.centresOfOrganization.newCentre');
            this.breadcrumbService.set(
                '@' + BreadcrumbsEnum.Center,
                BreadcrumbsEnum.Center + '|' + centerName,
            );
            this.store.globalStore?.dispatch(updateVisitHistoryData());
        });
    }

    ngOnDestroy(): void {
        this.centerSubscription.unsubscribe();
    }

    protected async saveManagers(centerId: number): Promise<void> {
        await this.store.replaceManagers(centerId, this.managersForm.getRawValue().userIds ?? []);
        this.managersForm.markAsPristine();
        contentGlobalSuccess.next('common.updated');
    }

    private async loadManagers(centerId: number): Promise<void> {
        this.managersForm.patchValue({
            userIds: await this.store.getManagerIds(centerId),
        });
        this.managersForm.markAsPristine();
    }
}
